import { Overlay } from "@angular/cdk/overlay";
import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  HostListener,
  NgZone,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren,
} from "@angular/core";
import { MAT_MENU_SCROLL_STRATEGY } from "@angular/material/menu";
import { LoggingService, ScrollService, WindowService } from "@merri/common";
import { Subject, from } from "rxjs";
import { auditTime, distinctUntilChanged, filter, map, switchMap, take, takeUntil } from "rxjs/operators";
import { SessionService } from "src/app/core";
import { EventContextService, EventVenuesService } from "src/app/features/events";
import { OnboardingService, OnboardingWorkflowStepTypes } from "src/app/features/onboarding";
import { HeaderService, LayoutService } from "src/app/layout";
import { HelperMethods } from "src/app/shared";
import { VisionboardTemplate } from "../../models";
import { VisionboardService, VisionboardVersionsService, VisionboardWorkspaceTemplateService } from "../../services";
import { VisionboardGlobalItemSearchService } from "../../shared/services";

@Component({
  selector: "app-visionboard-workspace-templates-container",
  templateUrl: "./visionboard-workspace-templates-container.component.html",
  styleUrls: ["./visionboard-workspace-templates-container.component.scss"],
  providers: [
    {
      provide: MAT_MENU_SCROLL_STRATEGY,
      deps: [Overlay],
      useFactory: (overlay: Overlay) => () => overlay.scrollStrategies.block(),
    },
  ],
  standalone: false,
})
export class VisionboardWorkspaceTemplatesContainerComponent implements OnInit, OnDestroy {
  /** Map of template titles to normalized anchor ids */
  anchorMap: { [title: string]: string } = {};
  error: string;
  fixedPos: boolean;
  isSmallScreen: boolean;
  showRequestButton: boolean;
  showControls: boolean;

  /** site header height (70) plus padding to use as offset when jumping to an anchor */
  jumpOffset: number;
  loading: boolean;
  navTopGap: number;
  templates: VisionboardTemplate[];
  topTemplateTitle: string;
  eventVenueId: number;
  private destroyed$: Subject<void>;
  private resizeSubject$: Subject<void>;

  /** Remove non-alpha-num characters before using for html id attribute */
  private titleEscaper = /\W+/g;

  @ViewChildren("jumpCategory") jumpCategories: QueryList<ElementRef<HTMLSpanElement>>;
  @ViewChild("wrapper", { static: true }) wrapper: ElementRef;

  @HostListener("window:resize")
  onResize(): void {
    this.resizeSubject$.next();
  }

  constructor(
    private ngZone: NgZone,
    private visionboardTemplateService: VisionboardWorkspaceTemplateService,
    private visionboardVersionsService: VisionboardVersionsService,
    private layoutService: LayoutService,
    private windowService: WindowService,
    private scrollService: ScrollService,
    private sessionService: SessionService,
    private onboardingService: OnboardingService,
    private _headerService: HeaderService,
    private _cdr: ChangeDetectorRef,
    private _visionboardService: VisionboardService,
    private _eventContextService: EventContextService,
    private _visionboardGlobalItemSearchService: VisionboardGlobalItemSearchService,
    private _eventVenuesService: EventVenuesService,
  ) {
    LoggingService.log("VisionboardWorkspaceTemplatesContainerComponent - ctor");
    this.destroyed$ = new Subject<void>();
    this.resizeSubject$ = new Subject<void>();
  }

  ngOnInit(): void {
    this.resizeSubject$.pipe(auditTime(250), takeUntil(this.destroyed$)).subscribe({
      next: () => {
        // adjust on resize
        if (this.fixedPos) {
          this.setNavTopGap();
        }

        const firstInView = this.templates && this.templates.find(t => this.isTemplateInView(t));
        if (firstInView) {
          this.topTemplateTitle = firstInView.title;
        }
      },
    });

    this._headerService.headerHeight$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.setNavTopGap();
      },
    });

    this.setSmallScreenSub();

    this.loading = true;
    this.setNavTopGap();

    this.setTemplateSub();
    this.setScrollSub();
    this.setActiveClassSub();

    this.sessionService.sessionUser$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: () => {
        this.showRequestButton = this.sessionService.canMakeCustomRequests();
        this.showControls = !this._eventContextService.isGuestEvent();
      },
    });

    this._visionboardGlobalItemSearchService.itemSelect$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: item => {
        const foundTemplate = this.templates.find(t => t.lanes.find(l => l.categories[item.category]?.includes(item.subCategory)));

        if (foundTemplate) {
          this.jumpToHeader(foundTemplate);
        }
      },
    });

    this.eventVenueId = this._eventVenuesService.getFirstVenue()?.id;
  }

  ngOnDestroy(): void {
    LoggingService.log("VisionboardWorkspaceTemplatesContainerComponent - destroy");
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  setTemplateSub(): void {
    this.visionboardTemplateService.currentTemplates$.pipe(takeUntil(this.destroyed$)).subscribe(templates => {
      if (!templates[0]) {
        this.error = "FAILED TO LOAD VISION BOARD, TRY AGAIN";
        this.templates = null;
        this.loading = false;
        this.topTemplateTitle = null;
      } else {
        this.error = null;
        this.templates = templates[1];
        this.topTemplateTitle = this.templates && this.templates[0] && this.templates[0].title;
        this.loading = !this.templates && !!this.visionboardVersionsService.getSelectedVersion();
        this.anchorMap = this.makePageAnchorIds(this.templates);

        this._visionboardService.setLoading(this.loading);

        // bit hacky but once loaded we scroll up by a single pixel to trigger our scroll events
        // this will ensure that the nav panel is aligned in case the user scrolls during load
        if (!this.loading) {
          setTimeout(() => window.scrollBy(0, -1));

          this.onboardingService.trigger(OnboardingWorkflowStepTypes.VbIntro);
        }
      }
    });
  }

  setSmallScreenSub(): void {
    this.windowService
      .observeMax$(1080)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(e => {
        this.isSmallScreen = e.matches;
      });
  }

  setScrollSub(): void {
    // Determine if sidenav needs to be fixed
    this.scrollService.scroll$
      .pipe(
        map(() => HelperMethods.getRect(this.wrapper.nativeElement).y - this.navTopGap <= 0),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe(b => {
        this.ngZone.run(() => {
          this.fixedPos = b;

          this.setNavTopGap();
        });
      });
  }

  setActiveClassSub(): void {
    this.scrollService.scroll$
      .pipe(
        filter(() => !!this.templates),
        switchMap(() =>
          from(this.templates).pipe(
            filter(t => this.isTemplateInView(t)),
            map(t => t.title),
            take(1),
          ),
        ),
        distinctUntilChanged(),
        takeUntil(this.destroyed$),
      )
      .subscribe(title => {
        this.ngZone.run(() => {
          this.topTemplateTitle = title;
        });
      });
  }

  jumpToHeader(t: VisionboardTemplate): void {
    // The menu closing causes scrolling to stop, this hacky timeout seems to fix it for now
    setTimeout(() => {
      this.jumpToHeaderInner(t);
    }, 250);
  }

  isActiveCategory(t: VisionboardTemplate): boolean {
    return t.title === this.topTemplateTitle;
  }

  private makePageAnchorIds(templates: VisionboardTemplate[]): { [t: string]: string } {
    return (templates || [])
      .map(t => t.title)
      .reduce((acc, title) => {
        acc[title] = this.generateElementId(title);
        return acc;
      }, {});
  }

  private generateElementId(t: string): string {
    return t.toLowerCase().replace(this.titleEscaper, "-");
  }

  private jumpToHeaderInner(t: VisionboardTemplate): void {
    const anchorId = this.anchorMap[t.title];
    const element = this.jumpCategories.find(e => e.nativeElement.id === anchorId);
    const topScroll = this.scrollService.getScrollPosition("top");

    this.setNavTopGap();

    const top = topScroll + element.nativeElement.getBoundingClientRect().top - this.jumpOffset;

    this.scrollService.scrollWindowTo(0, top);
  }

  private setNavTopGap(): void {
    this.navTopGap = this.layoutService.stickyHeaderHeight();
    this.jumpOffset = this.navTopGap + 10;
    this._cdr.detectChanges();
  }

  private isTemplateInView(t: VisionboardTemplate): boolean {
    const id = this.generateElementId(t.title);
    const el = this.jumpCategories.find(i => i.nativeElement.id === id);
    const rect = el.nativeElement.getBoundingClientRect();
    return this.windowService.isInView(rect);
  }
}
